"use client";

import { useGetCurrentUser } from "@/hooks/auth-hook";
import {
  useAddPlace,
  useCommuteToMyPlaces,
  useDeletePlace,
  useLifePlaces,
} from "@/hooks/life-hooks";
import { AnimatePresence, motion } from "framer-motion";
import {
  Briefcase,
  Car,
  Clock,
  Dumbbell,
  GraduationCap,
  Home,
  Loader2,
  MapPin,
  Plus,
  Route,
  Trash2,
} from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";

const ICONS: Record<string, any> = {
  work: Briefcase,
  school: GraduationCap,
  family: Home,
  gym: Dumbbell,
  pin: MapPin,
};
const ICON_OPTIONS = [
  { v: "work", ar: "العمل", en: "Work" },
  { v: "school", ar: "المدرسة", en: "School" },
  { v: "family", ar: "الأهل", en: "Family" },
  { v: "gym", ar: "النادي", en: "Gym" },
  { v: "pin", ar: "آخر", en: "Other" },
];

export default function LifeMapCommute({
  latitude,
  longitude,
}: {
  latitude?: number;
  longitude?: number;
}) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const { data: user } = useGetCurrentUser();
  const isLoggedIn = !!user?.user;

  const { data: places = [] } = useLifePlaces(isLoggedIn);
  const commute = useCommuteToMyPlaces();
  const addPlace = useAddPlace();
  const delPlace = useDeletePlace();

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: "", address: "", icon: "work" });

  // auto-calc when places & coords are ready
  useEffect(() => {
    if (isLoggedIn && places.length > 0 && latitude && longitude) {
      commute.mutate({ fromLat: latitude, fromLng: longitude });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places.length, latitude, longitude, isLoggedIn]);

  // hidden entirely unless the user is logged in
  if (!latitude || !longitude || !isLoggedIn) return null;

  const results = commute.data || [];

  const submitPlace = () => {
    if (!form.label.trim() || !form.address.trim()) return;
    addPlace.mutate(form, {
      onSuccess: () => {
        setForm({ label: "", address: "", icon: "work" });
        setShowAdd(false);
      },
    });
  };

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="mt-8 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="size-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shrink-0">
            <Route size={20} />
          </div>
          <div>
            <h3 className="font-black text-slate-900 text-base">
              {isAr ? "خريطة حياتك اليومية" : "Your Daily Life Map"}
            </h3>
            <p className="text-xs text-slate-400">
              {isAr
                ? "كم يبعد هذا العقار عن أماكنك المهمة؟"
                : "How far is this property from the places that matter?"}
            </p>
          </div>
        </div>
        {isLoggedIn && (
          <button
            onClick={() => setShowAdd((s) => !s)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 text-emerald-700 font-bold text-sm px-3 py-2 hover:bg-emerald-100 transition-colors"
          >
            <Plus size={15} /> {isAr ? "أضف مكان" : "Add place"}
          </button>
        )}
      </div>

      {/* logged-in content */}
      <>
          {/* add form */}
          <AnimatePresence>
            {showAdd && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-4"
              >
                <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    {ICON_OPTIONS.map((o) => {
                      const Ic = ICONS[o.v];
                      return (
                        <button
                          key={o.v}
                          onClick={() => setForm({ ...form, icon: o.v })}
                          className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold border transition-all ${
                            form.icon === o.v
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-white text-slate-500 border-slate-200"
                          }`}
                        >
                          <Ic size={14} /> {isAr ? o.ar : o.en}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder={isAr ? "الاسم (مثال: شركتي)" : "Label (e.g. My office)"}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                  />
                  <input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder={isAr ? "العنوان (مثال: حي العليا، الرياض)" : "Address (e.g. Olaya, Riyadh)"}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                  />
                  <button
                    onClick={submitPlace}
                    disabled={addPlace.isPending}
                    className="w-full rounded-xl bg-emerald-600 text-white font-bold py-2.5 text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addPlace.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    {isAr ? "إضافة" : "Add"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {places.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              {isAr ? "أضف أماكنك المهمة لحساب المسافة إليها." : "Add your key places to see commute times."}
            </p>
          ) : commute.isPending ? (
            <div className="flex justify-center py-6">
              <Loader2 className="animate-spin text-emerald-600" size={24} />
            </div>
          ) : (
            <div className="space-y-2.5">
              {results.map((r: any, idx: number) => {
                const Ic = ICONS[r.icon] || MapPin;
                const fastest = idx === 0;
                return (
                  <div
                    key={r.id}
                    className={`flex items-center gap-3 rounded-2xl border p-3.5 ${
                      fastest ? "border-emerald-200 bg-emerald-50/50" : "border-slate-100 bg-white"
                    }`}
                  >
                    <div className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${fastest ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                      <Ic size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 text-sm truncate">{r.label}</p>
                      <p className="text-xs text-slate-400 truncate">{r.address}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="flex items-center gap-1 font-black text-slate-800 justify-end">
                        <Clock size={14} className="text-emerald-600" />
                        {r.durationMin} {isAr ? "د" : "min"}
                      </p>
                      <p className="text-[11px] text-slate-400 flex items-center gap-1 justify-end">
                        <Car size={11} /> {r.distanceKm} {isAr ? "كم" : "km"}
                        {r.approx ? "≈" : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => delPlace.mutate(r.id)}
                      className="text-slate-300 hover:text-red-500 transition-colors shrink-0"
                      title={isAr ? "حذف" : "Delete"}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
      </>
    </div>
  );
}
